import { useRef, useState } from "react";
import { Platform } from "react-native";

import type { AnalyticsProperties } from "@/lib/analytics/analytics";
import { trackEvent } from "@/lib/analytics/posthogAnalytics";
import { reportCaughtError } from "@/lib/analytics/reportError";

export const SEARCH_OUTCOMES = [
  ["useful", "Found prices"],
  ["no_results", "No results"],
  ["wrong_matches", "Wrong matches"],
  ["access_problem", "Couldn't access"],
  ["skipped", "Not now"],
] as const;
export type SearchOutcome = (typeof SEARCH_OUTCOMES)[number][0];
type Attempt = { url: string; properties: AnalyticsProperties; };

/** Correlates outbound searches with explicit feedback; a handoff never implies useful results. */
export function useMarketplaceSearch({ marketplace, url, properties, track }: {
  marketplace: "ebay" | "fanatics" | "alt";
  url: string;
  properties: AnalyticsProperties;
  track: boolean;
}) {
  const current = useRef<Attempt | null>(null);
  const [pending, setPending] = useState<Attempt | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  function start() {
    setFailedUrl(null);
    if (!track) return null;
    const attempt = {
      url,
      properties: {
        ...properties,
        marketplace,
        os: Platform.OS,
        builder_version: 1,
        attempt_id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      },
    };
    current.current = attempt;
    setPending(attempt);
    if (marketplace === "ebay") trackEvent("ebay_solds_search_started", attempt.properties);
    else if (marketplace === "fanatics") {
      trackEvent("fanatics_solds_search_started", attempt.properties);
    } else trackEvent("alt_solds_search_started", attempt.properties);
    return attempt;
  }

  function handoff(attempt: Attempt | null, result: string) {
    if (!attempt || !["opened", "dismiss", "cancel"].includes(result)) return;
    trackEvent("marketplace_search_handoff", { ...attempt.properties, browser_result: result });
  }

  function fail(attempt: Attempt | null) {
    if (attempt) {
      trackEvent("marketplace_search_open_failed", {
        ...attempt.properties,
        failure_reason: "browser_open_failed",
      });
      reportCaughtError(new Error("Could not open marketplace search"), {
        marketplace,
        attempt_id: attempt.properties.attempt_id,
        platform: attempt.properties.platform,
      });
    }
    if (!attempt || current.current === attempt) {
      current.current = null;
      setPending(null);
      setFailedUrl(url);
    }
  }

  function respond(outcome: SearchOutcome) {
    const attempt = current.current;
    if (!track || !attempt || attempt.url !== url) return;
    current.current = null;
    setPending(null);
    trackEvent("marketplace_search_feedback", { ...attempt.properties, outcome });
  }

  return {
    start,
    handoff,
    fail,
    respond,
    pending: track && pending?.url === url,
    failed: failedUrl === url,
  };
}
